import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import BackgroundFX from '../components/backgroundFX'
import FormField from '../components/formField'
import Logo from '../components/logo'
import ThemeToggle from '../components/themeToggle'
import { api } from '../data/api'

// step: 'email' → 'otp' → 'password' → 'done'

export default function ForgotPasswordPage() {
  const navigate = useNavigate()
  const [step,     setStep]     = useState('email')
  const [email,    setEmail]    = useState('')
  const [userId,   setUserId]   = useState(null)
  const [otp,      setOtp]      = useState('')
  const [password, setPassword] = useState('')
  const [confirm,  setConfirm]  = useState('')
  const [error,    setError]    = useState('')
  const [loading,  setLoading]  = useState(false)
  const [resendMsg,setResendMsg]= useState('')
  const [resending,setResending]= useState(false)

  async function handleEmailSubmit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const res = await api.post('/api/auth/forgotPassword', { email })
      setUserId(res.userId)
      setStep('otp')
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function handleOtpSubmit(e) {
    e.preventDefault()
    setError('')
    if (otp.length !== 6) return
    // Just move forward — OTP verified on password reset
    setStep('password')
  }

  async function handleResend() {
    setError('')
    setResending(true)
    try {
      const res = await api.post('/api/auth/forgotPassword', { email })
      setUserId(res.userId)
      setResendMsg('OTP resent! Check your email.')
      setTimeout(() => setResendMsg(''), 4000)
    } catch (err) {
      setError(err.message)
    } finally {
      setResending(false)
    }
  }

  async function handlePasswordSubmit(e) {
    e.preventDefault()
    setError('')
    if (password !== confirm) { setError('Passwords do not match.'); return }
    setLoading(true)
    try {
      await api.post('/api/auth/resetPassword', { userId, otp, password })
      setStep('done')
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative"
         style={{ background: 'var(--bg)' }}>
      <BackgroundFX />
      <ThemeToggle fixed />

      <div className="relative z-10 w-full sm:max-w-[60%]">
        <div className="card p-10 sm:p-12 sm:px-20">
          <div className="text-center mb-6"><Logo size="auth" /></div>

          {/* ── Step: Email ── */}
          {step === 'email' && (
            <>
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold mb-1" style={{ color: 'var(--text-1)' }}>Forgot password?</h2>
                <p className="text-sm" style={{ color: 'var(--text-3)' }}>
                  Enter your registered email and we'll send you an OTP to reset your password.
                </p>
              </div>
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <FormField label="Email" type="email" value={email} onChange={setEmail}
                  placeholder="you@example.com" autoComplete="email" maxLength={100} />
                {error && <p className="text-xs px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30" style={{ color: 'var(--text-1)' }}>{error}</p>}
                <button type="submit" disabled={loading || !email.trim()} className="btn-primary w-full justify-center disabled:opacity-60">
                  {loading
                    ? <><span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />Sending…</>
                    : 'Send OTP'}
                </button>
              </form>
              <p className="text-xs text-center mt-5" style={{ color: 'var(--text-3)' }}>
                Remember it?{' '}
                <Link to="/login" className="font-medium hover:underline" style={{ color: 'var(--brand)' }}>Back to login</Link>
              </p>
            </>
          )}

          {/* ── Step: OTP ── */}
          {step === 'otp' && (
            <>
              <h2 className="text-lg font-bold text-center mb-1" style={{ color: 'var(--text-1)' }}>Check your email</h2>
              <p className="text-sm text-center mb-6" style={{ color: 'var(--text-3)' }}>
                We sent a 6-digit OTP to <span className="font-semibold" style={{ color: 'var(--text-1)' }}>{email}</span>
              </p>
              <form onSubmit={handleOtpSubmit} className="space-y-4">
                <input
                  type="text" inputMode="numeric" maxLength={6}
                  value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="Enter 6-digit OTP"
                  className="w-full px-4 py-3 rounded-xl text-sm text-center tracking-widest font-bold outline-none transition-all"
                  style={{ background: 'var(--surface-2)', color: 'var(--text-1)', boxShadow: '0 0 0 1px var(--border)', letterSpacing: '0.3em' }}
                  onFocus={e => e.currentTarget.style.boxShadow = '0 0 0 2px var(--brand)'}
                  onBlur={e  => e.currentTarget.style.boxShadow = '0 0 0 1px var(--border)'}
                />
                {error && <p className="text-xs px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30" style={{ color: 'var(--text-1)' }}>{error}</p>}
                {resendMsg && <p className="text-xs text-center" style={{ color: '#34d399' }}>{resendMsg}</p>}
                <button type="submit" disabled={otp.length !== 6} className="btn-primary w-full justify-center disabled:opacity-60">
                  Continue
                </button>
              </form>
              <p className="text-xs text-center mt-4 flex items-center justify-center gap-1.5" style={{ color: 'var(--text-3)' }}>
                Didn't receive it?{' '}
                <button onClick={handleResend} disabled={resending} className="font-medium hover:underline flex items-center gap-1 disabled:opacity-60" style={{ color: 'var(--brand)' }}>
                  {resending && <span className="w-3 h-3 rounded-full border-2 animate-spin flex-shrink-0" style={{ borderColor: 'color-mix(in srgb, var(--brand) 30%, transparent)', borderTopColor: 'var(--brand)' }} />}
                  Resend OTP
                </button>
              </p>
            </>
          )}

          {/* ── Step: New Password ── */}
          {step === 'password' && (
            <>
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold mb-1" style={{ color: 'var(--text-1)' }}>Set new password</h2>
                <p className="text-sm" style={{ color: 'var(--text-3)' }}>Choose a new password for your account.</p>
              </div>
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <FormField label="New Password" type="password" value={password} onChange={setPassword}
                  placeholder="New password" autoComplete="new-password" maxLength={20} />
                <FormField label="Confirm Password" type="password" value={confirm} onChange={setConfirm}
                  placeholder="Confirm new password" autoComplete="new-password" maxLength={20} />
                {error && <p className="text-xs px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30" style={{ color: 'var(--text-1)' }}>{error}</p>}
                <button type="submit" disabled={loading || !password || !confirm} className="btn-primary w-full justify-center disabled:opacity-60">
                  {loading
                    ? <><span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />Resetting…</>
                    : 'Reset Password'}
                </button>
              </form>
            </>
          )}

          {/* ── Step: Done ── */}
          {step === 'done' && (
            <div className="text-center">
              <div className="w-14 h-14 rounded-full flex items-center justify-center text-2xl mx-auto mb-4"
                   style={{ background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.3)' }}>✓</div>
              <h2 className="text-lg font-bold mb-2" style={{ color: 'var(--text-1)' }}>Password reset!</h2>
              <p className="text-sm mb-6" style={{ color: 'var(--text-3)' }}>
                Your password has been updated. You can now log in with your new password.
              </p>
              <button onClick={() => navigate('/login')} className="btn-primary w-full justify-center">
                Back to Login
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}
